// P11: treatments attached to a program assignment. The API stores them in their
// own table and sends them as `treatments: [{ id, label, from_day, to_day }]` on
// the assignment (Program resource) and on a protocol read through it (calendar,
// current protocols of a horse). Day 1 is the assignment's start_at, both ends
// included. Pure, no '@/' import: tested with `node --test`.

import { calendarDaysBetween, parseLocalDay, toPositiveInt } from './program-days.js'
import { programPayload, programToForm } from '../program-form.js'

// The API's limits (StoreProgramRequest).
export const MAX_TREATMENTS = 20
export const MAX_TREATMENT_DAY = 365
export const MAX_TREATMENT_LABEL = 255

const list = (treatments) => (Array.isArray(treatments) ? treatments : [])

// Treatments due on a program day (1 = start day), both ends of a window included.
// A day outside the program (before it, or not a day at all) has none, and so has a
// treatment without a readable window: never shown on a day it cannot be proven for.
export const treatmentsOnDay = (treatments, programDay) => {
    const day = toPositiveInt(programDay)
    if (day === null) return []

    return list(treatments).filter((treatment) => {
        const from = toPositiveInt(treatment?.from_day)
        const to = toPositiveInt(treatment?.to_day)
        if (from === null || to === null) return false
        return day >= from && day <= to
    })
}

// The labels of a day's treatments, the way a line or a chip lists them.
export const treatmentLabels = (treatments) =>
    list(treatments)
        .map((treatment) => String(treatment?.label ?? '').trim())
        .filter(Boolean)
        .join(', ')

// Program day of a calendar date: 1 on start_at, 0 the day before, and so on.
// Counted on local calendar days through UTC, so a DST change never shifts it.
export const programDayOn = (startAt, date = new Date()) => {
    const start = parseLocalDay(startAt)
    const target = date instanceof Date ? date : parseLocalDay(date)
    if (!start || !target || !Number.isFinite(target.getTime())) return null
    return calendarDaysBetween(start, target) + 1
}

// What the calendar card of a program shows on day `dayIndex` of its event. The
// event of /user/events runs until the last treatment day when a treatment
// outlives the program; the protocol it nests keeps the program's own length in
// `day_count` (its end_at is the day after the last one, not the last one). Days
// within the program read "n/N" exactly as before (N = the program's day count);
// a day that only exists for a treatment hides that label (`treatmentOnly`).
export const calendarProgramDay = ({ dayIndex, eventDays, protocol } = {}) => {
    const programDays = toPositiveInt(protocol?.day_count) ?? eventDays ?? null
    const treatmentOnly = programDays !== null && dayIndex > programDays

    return {
        day: !treatmentOnly && programDays > 1 ? `${dayIndex}/${programDays}` : null,
        treatmentOnly,
        treatments: treatmentsOnDay(protocol?.treatments, dayIndex),
    }
}

// Rows the editor works on, from what the API sent (ids and extra fields dropped).
export const treatmentsToForm = (treatments) =>
    list(treatments).map((treatment) => {
        const from = toPositiveInt(treatment?.from_day) ?? 1
        return {
            label: String(treatment?.label ?? ''),
            from_day: from,
            to_day: toPositiveInt(treatment?.to_day) ?? from,
        }
    })

// A new row: the whole program by default, as in the PR #1258 editor.
export const emptyTreatment = (totalDays = 1) => {
    const to = Math.min(toPositiveInt(totalDays) ?? 1, MAX_TREATMENT_DAY)
    return { label: '', from_day: 1, to_day: to }
}

// What POST/PUT horses/{h}/programs accepts under `treatments`. A row left
// without a label is dropped (nothing to take); the days go as numbers.
export const treatmentsPayload = (rows) =>
    list(rows)
        .filter((row) => String(row?.label ?? '').trim())
        .map((row) => ({
            label: String(row.label).trim(),
            from_day: Number(row.from_day),
            to_day: Number(row.to_day),
        }))

const validDay = (value) => {
    const day = toPositiveInt(value)
    return day !== null && day <= MAX_TREATMENT_DAY
}

// Why a row cannot be sent, or null. Rows without a label are not checked:
// they are dropped from the payload.
export const treatmentRowError = (row) => {
    if (!String(row?.label ?? '').trim()) return null
    if (String(row.label).trim().length > MAX_TREATMENT_LABEL) return 'label'
    if (!validDay(row.from_day) || !validDay(row.to_day)) return 'days'
    if (Number(row.to_day) < Number(row.from_day)) return 'order'
    return null
}

// The first reason the list cannot be sent, or null: the same rules as the API,
// checked before the request so the form says what to fix.
export const treatmentsError = (rows) => {
    if (treatmentsPayload(rows).length > MAX_TREATMENTS) return 'too-many'
    for (const row of list(rows)) {
        const error = treatmentRowError(row)
        if (error) return error
    }
    return null
}

// PUT horses/{h}/programs/{id} from the program page's "Treatments" button: the
// API requires start_at and protocol_id on every update; the other fields stay
// out so they are left as they are, and the treatments replace the whole list.
export const programTreatmentsUpdatePayload = (program, rows, { protocolId = null } = {}) => {
    const payload = programPayload(programToForm(program, { protocolId }))
    return {
        start_at: payload.start_at,
        protocol_id: payload.protocol_id,
        treatments: treatmentsPayload(rows),
    }
}
